import type { AftercareInputs, AftercarePlan, TimelineStep } from '../data/types';

const SCENE_LABEL: Record<AftercareInputs['sceneType'], string> = {
  impact: 'impact',
  sensation: 'sensation',
  bondage: 'bondage',
  psychological: 'psychological play',
  primal: 'primal play',
  mixed: 'mixed play',
};

/**
 * Rule-based aftercare plan generator. Each input contributes specific
 * items and timeline steps. Order matters: more urgent care first.
 */
export function generatePlan(inputs: AftercareInputs): AftercarePlan {
  const items: string[] = [];
  const first30: TimelineStep[] = [];
  const nextHours: TimelineStep[] = [];

  // Universal items every plan benefits from
  items.push('a soft blanket within reach');
  items.push('water nearby');

  // Scene-type specifics
  if (inputs.sceneType === 'impact' || inputs.intensity === 'heavy' || inputs.intensity === 'edge') {
    items.push('arnica gel or a gentle topical for tender spots');
  }
  if (inputs.sceneType === 'bondage') {
    items.push('warm towel for restoring circulation');
    first30.push({
      time: 'immediately',
      action: 'check skin, pulse, and any tingling in limbs that were bound',
    });
    first30.push({
      time: 'within 5 minutes',
      action: 'gentle range-of-motion stretches before sitting up',
    });
  }
  if (inputs.sceneType === 'primal') {
    items.push('a cool damp cloth for the face');
    first30.push({
      time: 'first 10 minutes',
      action: 'breathe down slowly — count of 4 in, 6 out — until the body settles',
    });
  }
  if (inputs.sceneType === 'psychological' || inputs.emotionalWeight === 'heavy') {
    items.push('a quiet space with low lighting');
  }
  if (inputs.intensity === 'heavy' || inputs.intensity === 'edge') {
    items.push('a hot or warm drink (tea, broth, hot chocolate)');
    first30.push({
      time: 'within 10 minutes',
      action: 'small sips of something warm and sweet to rebalance blood sugar',
    });
  }

  // Physical needs
  if (inputs.physicalNeeds.includes('warmth')) {
    items.push('extra layers in case body temperature drops');
    first30.push({ time: 'first 5 minutes', action: 'wrap in blankets, even if not cold yet' });
  }
  if (inputs.physicalNeeds.includes('hydration')) {
    items.push('a full water bottle plus an electrolyte option');
    first30.push({
      time: 'within 15 minutes',
      action: 'sip water — slowly. half a glass minimum.',
    });
  }
  if (inputs.physicalNeeds.includes('food')) {
    items.push('something simple and warm: broth, toast, fruit, crackers');
    first30.push({
      time: 'within 20 minutes',
      action: 'small portion of food — slowly, with attention',
    });
  }
  if (inputs.physicalNeeds.includes('sleep')) {
    nextHours.push({
      time: 'within 2 hours',
      action: 'rest or sleep — even a nap. the body needs to integrate.',
    });
  }
  if (inputs.physicalNeeds.includes('movement')) {
    nextHours.push({
      time: 'within 2 hours',
      action: 'short walk or gentle stretch to release residual tension',
    });
  }

  // Emotional needs
  if (inputs.emotionalNeeds.includes('silence')) {
    first30.push({
      time: 'first 10 minutes',
      action: 'hold without words. let the silence do its work.',
    });
  }
  if (inputs.emotionalNeeds.includes('talking')) {
    first30.push({
      time: 'first 15 minutes',
      action: 'speak quietly about what just happened, what landed, what surprised',
    });
  }
  if (inputs.emotionalNeeds.includes('touch')) {
    first30.push({
      time: 'throughout the first 20 minutes',
      action: 'consistent, non-sexual physical contact — hand-holding, hair-stroking, holding',
    });
  }
  if (inputs.emotionalNeeds.includes('witnessing')) {
    first30.push({
      time: 'within 20 minutes',
      action: 'have someone simply be present without doing — eyes available, no agenda',
    });
  }
  if (inputs.emotionalNeeds.includes('solitude')) {
    first30.push({
      time: 'after first 10 minutes of grounding',
      action: 'time alone in a safe space — door closed, no one watching',
    });
  }

  // Duration-aware scaling for the "next hours"
  if (inputs.duration === '1-3-hours' || inputs.duration === 'longer') {
    nextHours.push({
      time: '1–2 hours after',
      action: 'check in again — body, mind, mood. ask: do I want anything else?',
    });
  }
  if (inputs.duration === 'longer') {
    nextHours.push({
      time: '3–4 hours after',
      action: 'second meal — heartier than the first',
    });
  }

  // Drop awareness (the next-days copy)
  const dropMap: Record<AftercareInputs['dropPattern'], string> = {
    rare:
      'drop is uncommon for you, but if you feel low or weepy in the next 24–72 hours, treat it as a normal echo of intensity, not a sign that anything is wrong.',
    occasional:
      'drop sometimes arrives in the next 1–3 days. when it does, it is not a verdict on what happened — it is your nervous system re-balancing. eat, sleep, and reach out if you need.',
    common:
      'drop will likely arrive in the next 1–3 days. plan for it: keep your schedule lighter than usual, have favorite foods within reach, and tell at least one person you might need them.',
    severe:
      'drop has been hard for you in the past. take this seriously: schedule a check-in with a trusted person 24 and 48 hours out, have safe foods ready, plan for an early bedtime, and consider professional support available.',
  };
  const nextDays = dropMap[inputs.dropPattern];

  // Time-available constraints — trim or warn
  if (inputs.timeAvailable === '15min') {
    items.push("a friendly note: 15 minutes is tight — prioritize warmth, water, and one moment of contact");
  }
  if (inputs.timeAvailable === 'overnight') {
    nextHours.push({
      time: 'before sleep',
      action: 'a slow ritual to mark the close — bath, candle, or simply turning off the phone',
    });
  }

  // Title
  const sceneLabel = SCENE_LABEL[inputs.sceneType];
  const title = inputs.recipeName?.trim()
    ? inputs.recipeName.trim()
    : `for ${sceneLabel}, at ${inputs.intensity} intensity`;

  // De-duplicate while preserving order
  const dedupe = <T extends { action?: string } | string>(arr: T[]): T[] => {
    const seen = new Set<string>();
    return arr.filter((x) => {
      const key = typeof x === 'string' ? x : x.action ?? '';
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  return {
    title,
    items: dedupe(items),
    first30: dedupe(first30),
    nextHours: dedupe(nextHours),
    nextDays,
    signature: 'filled by you · for you',
  };
}
